import styles from "./ride-list-item.module.less";
import { Icon, multipleClassNames, QueueTimeDisplay, SimplifyGatsbyQuery } from "@parksandresorts/core";
import { QueueTimeItem } from "@parksandresorts/webapiproxy-types";
import Label from "components/block-elements/label/label";
import borderPatterns from "styles/theming/border-patterns.module.less";
import SiteConfigurationStore from "components/stores/site-configuration-store";
import LinkWrapper from "components/block-elements/link-wrapper/link-wrapper";
import UiPicture from "@rnb/ui-picture";
import SpeechBubble from "components/block-elements/speech-bubble/speech-bubble";

const difficultyConfig = {
  Easy: {
    icon: "icon-easy",
    textKey: "DIFFICULTY_EASY",
  },
  Medium: {
    icon: "icon-medium",
    textKey: "DIFFICULTY_MEDIUM",
  },
  Hard: {
    icon: "icon-hard",
    textKey: "DIFFICULTY_HARD",
  },
};

const RideListItem = (
  props: SimplifyGatsbyQuery<Queries.RideBlockFragment> & {
    mobileCompactList: boolean;
    attractionQueueTime?: QueueTimeItem;
  },
) => {
  const {
    imageInList: img,
    borderPattern,
    componentTheme,
    minimumHeight,
    title,
    label,
    ageRequirement,
    separateTickets,
    noHeightRequirement,
    rideDifficultyLevel,
    attractionQueueTime,
  } = props;

  const linkUrl = props.pageLink?.slug;

  const rideDifficultyIcon: string | undefined = rideDifficultyLevel
    ? difficultyConfig[rideDifficultyLevel as keyof typeof difficultyConfig]?.icon
    : undefined;
  const rideDifficultyLabel: string | undefined = rideDifficultyLevel
    ? SiteConfigurationStore.getLabel(difficultyConfig[rideDifficultyLevel as keyof typeof difficultyConfig]?.textKey)
    : undefined;

  const noHeightRequirementLabel: string | undefined = SiteConfigurationStore.getLabel("NO_HEIGHT_LIMIT");
  const separateTicketsLabel: string | undefined = SiteConfigurationStore.getLabel("SEPARATE_TICKET");
  const mobileCompactList = props.mobileCompactList;

  const getPicture = () => {
    return {
      id: `${props.contentful_id}_${props.id}`,
      src: `${img.file.url}?w=310&q=70`,
      alt: img.description ?? "",
      loading: "lazy",
      decode: "async",
      width: img.file.details?.image?.width,
      height: img.file.details?.image?.width,
      sources: [
        {
          srcset: `${img.file.url}?w=310&q=70&fm=webp, ${img.file.url}?w=600&q=70&fm=webp 2x`,
          type: "image/webp",
        },
        {
          srcset: `${img.file.url}?w=310&q=70, ${img.file.url}?w=600&q=70&fm=webp 2x`,
        },
      ],
    };
  };

  const getRequirementLabel = () => {
    if (minimumHeight) return minimumHeight;
    if (ageRequirement) return ageRequirement;
    if (noHeightRequirement && noHeightRequirementLabel) return noHeightRequirementLabel;
    return null;
  };

  return (
    <div className={multipleClassNames(componentTheme && `component-theme-${componentTheme}`)}>
      <div
        className={multipleClassNames(
          mobileCompactList && styles.mobileCompactList,
          borderPatterns[borderPattern + "_card"],
          mobileCompactList && styles.mobileCompact,
          styles.wrapper,
        )}
      >
        <LinkWrapper className={styles.link} url={linkUrl}>
          <div className={styles.labelWrapper} data-has-queue-time={!!attractionQueueTime}>
            {label && <Label title={label} />}
          </div>
          <div className={styles.imageWrapper}>
            {img ? <UiPicture {...getPicture()} classNamePicture={styles.picture} /> : ""}
          </div>
          <div className={styles.textWrapper} data-has-queue-time={!!attractionQueueTime}>
            {title && <h4 className={styles.headline}>{title}</h4>}
            {(ageRequirement || noHeightRequirement || minimumHeight) && (
              <div className={multipleClassNames(styles.propertyRow, styles.minHeight)}>
                <Icon name="length" className={styles.icon} />
                <span className="body-s">{getRequirementLabel()}</span>
              </div>
            )}
            {(rideDifficultyLabel || separateTickets) && (
              <div className={multipleClassNames(styles.propertyRow, styles.difficultyLevel)}>
                {rideDifficultyIcon && <span className={multipleClassNames(rideDifficultyIcon, styles.icon)} />}
                <span className="body-s">
                  {rideDifficultyLabel && <span className={styles.difficultyLevelLabel}>{rideDifficultyLabel}</span>}
                  {separateTickets && <span className={styles.separateTicketLabel}>{separateTicketsLabel}</span>}
                </span>
              </div>
            )}
            {attractionQueueTime && (
              <div className={styles.propertyRow}>
                <Icon name="clock" className={styles.icon} />
                <QueueTimeDisplay
                  state={attractionQueueTime.state}
                  queueTimeText={attractionQueueTime.queueTimeText}
                  variant="SPAN"
                  className="body-s"
                />
              </div>
            )}
          </div>
        </LinkWrapper>
      </div>

      {mobileCompactList && label && <SpeechBubble speechBubbleInverseColors={true} title={label} mobileOnly />}
    </div>
  );
};

export default RideListItem;
